import {
  Box,
  Text,
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Button,
  TextInput,
  Heading,
  Spinner,
} from "grommet";
import { Search } from "grommet-icons";
import { PSHRAByStatus } from "../graphql/queries";
import { useState, useEffect, useCallback } from "react";
import { API } from "aws-amplify";
import { UpdatePSHRAExam } from "../graphql/mutations";

const PAGE_LIMIT = 100;
const SEARCH_DEBOUNCE_MS = 350;

export default function PSHRA() {
  const [exams, setExams] = useState(null);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [matchedExams, setMatchedExams] = useState([]);
  const [loadingExams, setLoadingExams] = useState(false);
  const [selectedExam, setSelectedExam] = useState(null);

  function parseExamCode(examCode) {
    if (!examCode) return "";

    const parts = examCode.split("|");
    if (parts.length === 0 || !parts[0]) {
      return examCode;
    }

    const codePart = parts[0];
    const match = codePart.match(/^([A-Z]+)(\d+)$/);
    if (!match) return examCode;

    const [, prefix, numbers] = match;
    const formattedNumbers = numbers.match(/.{1,3}/g).join("-");

    return `${prefix}-${formattedNumbers}`;
  }

  const fetchAllExams = useCallback(async () => {
    setLoadingExams(true);
    try {
      const accumulated = [];
      let nextToken = null;

      do {
        const variables = {
          status: "QUEUE",
          limit: PAGE_LIMIT,
        };
        if (nextToken) {
          variables.nextToken = nextToken;
        }

        const response = await API.graphql({
          query: PSHRAByStatus,
          variables,
        });

        const page = response.data.pshraByStatus;
        if (page?.items?.length) {
          accumulated.push(...page.items);
        }
        nextToken = page?.nextToken || null;
      } while (nextToken);

      console.log({ totalQueued: accumulated.length });
      setExams(accumulated);
    } catch (error) {
      console.log(error);
      setExams([]);
    } finally {
      setLoadingExams(false);
    }
  }, []);

  useEffect(() => {
    fetchAllExams();
  }, [fetchAllExams]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    if (!exams) {
      setMatchedExams([]);
      return;
    }

    const q = search.trim().toLowerCase();
    if (!q) {
      setMatchedExams([]);
      return;
    }

    setMatchedExams(
      exams.filter((exam) => {
        const first = (exam.first_name || "").toLowerCase();
        const last = (exam.last_name || "").toLowerCase();
        const email = (exam.email || "").toLowerCase();
        return first.includes(q) || last.includes(q) || email.includes(q);
      })
    );
  }, [exams, search]);

  async function finishExam() {
    try {
      const data = await API.graphql({
        query: UpdatePSHRAExam,
        variables: {
          input: {
            session_id: selectedExam.session_id,
            status: "FINISHED",
          },
        },
      });
      console.log(data);

      if (!data.data.updatePSHRAExam.errors) {
        setSelectedExam(null);
        fetchAllExams();
      }
    } catch (error) {
      console.log(error);
    }
  }

  const queueEmpty =
    !loadingExams && exams && exams.length === 0;
  const noMatches =
    !loadingExams &&
    exams &&
    exams.length > 0 &&
    matchedExams.length === 0 &&
    search.trim().length > 0;

  return (
    <Box fill background="background-back" pad={{ horizontal: "medium", vertical: "large" }}>
      <Box width="full" gap="large" alignSelf="center" style={{ maxWidth: "1200px" }}>
        <Box gap="small">
          <Heading level={2} margin="none" color="brand">
            PSHRA exam queue
          </Heading>
          <Text color="dark-4" size="medium">
            Load the full queue, search by name or email, then select a tester to view details and
            finish.
          </Text>
        </Box>

        <Box
          direction="row-responsive"
          gap="large"
          align="start"
          wrap
        >
          <Box
            flex
            basis="medium"
            gap="medium"
            background="background-front"
            pad="medium"
            round="small"
            elevation="small"
            style={{ minWidth: "min(100%, 320px)" }}
          >
            <Text weight="bold" size="small" color="dark-3" textTransform="uppercase">
              Search queue
            </Text>
            <TextInput
              icon={<Search size="small" />}
              placeholder="First name, last name, or email"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              a11yTitle="Search by first name, last name, or email"
            />
            {searchInput !== search && searchInput.length > 0 && (
              <Text size="xsmall" color="dark-4">
                Updating results…
              </Text>
            )}

            <Text size="small" color="dark-4">
              {loadingExams
                ? "Loading queue…"
                : exams && exams.length > 0 && search.trim().length === 0
                  ? `${exams.length} in queue — type above to search`
                  : `${matchedExams.length} ${
                      matchedExams.length === 1 ? "match" : "matches"
                    }${exams ? ` of ${exams.length} in queue` : ""}`}
            </Text>

            <Box
              overflow="auto"
              flex={false}
              style={{
                maxHeight: "min(60vh, 520px)",
                minHeight: "min(40vh, 360px)",
              }}
              gap="small"
            >
              {loadingExams && (
                <Box align="center" justify="center" pad="large" gap="small">
                  <Spinner />
                  <Text size="small" color="dark-4">
                    Fetching all queued exams…
                  </Text>
                </Box>
              )}

              {queueEmpty && (
                <Box pad="medium" background="light-2" round="xsmall">
                  <Text textAlign="center" color="dark-4">
                    No exams in the queue.
                  </Text>
                </Box>
              )}

              {noMatches && (
                <Box pad="medium" background="light-2" round="xsmall" flex={false}>
                  <Text textAlign="center" color="dark-4">
                    No results for &quot;{search.trim()}&quot;. Try another name or email.
                  </Text>
                </Box>
              )}

              {!loadingExams &&
                matchedExams.length > 0 &&
                matchedExams.map((exam) => {
                  const isSelected = selectedExam?.session_id === exam.session_id;
                  return (
                    <Box key={exam.session_id} flex={false}>
                    <Card
                      background={isSelected ? "active" : "background-front"}
                      border={
                        isSelected
                          ? { color: "brand", size: "2px" }
                          : { color: "border", size: "1px" }
                      }
                      round="small"
                      elevation={isSelected ? "medium" : "xsmall"}
                      style={{ flexShrink: 0 }}
                    >
                      <CardBody pad="small">
                        <Box gap="xsmall">
                          <Text weight="bold" size="small" color="dark-3">
                            Name
                          </Text>
                          <Text size="small">
                            {exam.first_name} {exam.last_name}
                          </Text>
                          <Text weight="bold" size="small" color="dark-3" margin={{ top: "xsmall" }}>
                            Email
                          </Text>
                          <Text size="small" truncate>
                            {exam.email}
                          </Text>
                          <Text weight="bold" size="small" color="dark-3" margin={{ top: "xsmall" }}>
                            iMIS ID
                          </Text>
                          <Text size="small">{exam.iMIS_id}</Text>
                          <Text weight="bold" size="small" color="dark-3" margin={{ top: "xsmall" }}>
                            Exam
                          </Text>
                          <Text size="small">{exam.exam_name}</Text>
                        </Box>
                        <CardFooter pad={{ top: "small", horizontal: "none", bottom: "none" }}>
                          <Button
                            label={isSelected ? "Selected" : "Select"}
                            onClick={() => setSelectedExam(exam)}
                            primary={!isSelected}
                            disabled={isSelected}
                            size="small"
                          />
                        </CardFooter>
                      </CardBody>
                    </Card>
                    </Box>
                  );
                })}
            </Box>
          </Box>

          <Box flex basis="medium" gap="medium" style={{ minWidth: "min(100%, 280px)" }}>
            {!selectedExam && (
              <Box
                pad="large"
                background="background-front"
                round="small"
                elevation="small"
                align="center"
                justify="center"
              >
                <Text textAlign="center" color="dark-4">
                  Select someone from the queue to view their exam code and mark them finished.
                </Text>
              </Box>
            )}

            {selectedExam && (
              <Card
                width="full"
                background="background-front"
                elevation="small"
                round="small"
              >
                <CardHeader pad="medium" background="brand">
                  <Box gap="xsmall" fill="horizontal">
                    <Text weight="bold" size="large" color="white">
                      {selectedExam.first_name} {selectedExam.last_name}
                    </Text>
                    <Text size="small" color="white" style={{ opacity: 0.9 }}>
                      {selectedExam.email}
                    </Text>
                  </Box>
                </CardHeader>
                <CardBody pad="medium" gap="medium">
                  <Box
                    direction="row"
                    gap="small"
                    wrap
                    pad="small"
                    background="light-1"
                    round="xsmall"
                  >
                    <Box flex="shrink">
                      <Text size="xsmall" weight="bold" color="dark-4">
                        iMIS ID
                      </Text>
                      <Text>{selectedExam.iMIS_id}</Text>
                    </Box>
                    <Box flex="grow">
                      <Text size="xsmall" weight="bold" color="dark-4">
                        Exam name
                      </Text>
                      <Text>{selectedExam.exam_name}</Text>
                    </Box>
                  </Box>
                  <Box
                    pad="medium"
                    background="light-2"
                    round="xsmall"
                    align="center"
                  >
                    <Text size="xsmall" weight="bold" color="dark-4" margin={{ bottom: "xsmall" }}>
                      Exam code
                    </Text>
                    <Text size="xlarge" weight="bold" color="brand">
                      {parseExamCode(selectedExam.exam_code)}
                    </Text>
                  </Box>
                </CardBody>
                <CardFooter pad="medium" background="transparent">
                  <Button label="Finish tester" onClick={finishExam} primary />
                </CardFooter>
              </Card>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
